import React, { useEffect, useState } from "react";
import { VirtualItem } from "@tanstack/react-virtual";
import { CalendarDefinition, Dependency, EventOption } from "../../types/public-types";
import { BarTask } from "../../types/bar-task";
import { Arrow } from "../other/arrow";
import { handleTaskBySVGMouseEvent, taskXCoordinate } from "../../helpers/bar-helper";
import { isKeyboardEvent } from "../../helpers/other-helper";
import { TaskItem } from "../task-item/task-item";
import {
  BarMoveAction,
  GanttContentMoveAction,
  GanttEvent,
} from "../../types/gantt-task-actions";
import { Milestone } from "../task-item/milestone/milestone";
import { snapToWorkingTime, getWorkingIntervals } from "../../helpers/calendar-helper";
import { startOfDate, addToDate } from "../../helpers/date-helper";

export type TaskGanttContentProps = {
  tasks: BarTask[];
  dates: Date[];
  ganttEvent: GanttEvent;
  selectedTask: BarTask | undefined;
  rowHeight: number;
  columnWidth: number;
  timeStep: number;
  svg?: React.RefObject<SVGSVGElement>;
  svgWidth: number;
  taskHeight: number;
  arrowColor: string;
  arrowIndent: number;
  fontSize: string;
  fontFamily: string;
  rtl: boolean;
  virtualItems?: VirtualItem[];
  visibleStartY: number;
  visibleEndY: number;
  projectCalendar?: CalendarDefinition;
  setGanttEvent: (value: GanttEvent) => void;
  setFailedTask: (value: BarTask | null) => void;
  setSelectedTask: (taskId: string) => void;
  onBarTasksUpdate?: (task: BarTask) => void;
  sliderTime?: number;
} & EventOption;

export const TaskGanttContent: React.FC<TaskGanttContentProps> = ({
  tasks,
  dates,
  ganttEvent,
  selectedTask,
  rowHeight,
  columnWidth,
  timeStep,
  svg,
  taskHeight,
  arrowIndent,
  fontFamily,
  fontSize,
  rtl,
  virtualItems = [],
  setGanttEvent,
  setFailedTask,
  setSelectedTask,
  onBarTasksUpdate,
  onDateChange,
  onProgressChange,
  onDoubleClick,
  onClick,
  onDelete,
  onCalendarError,
  projectCalendar,
  visibleStartY,
  visibleEndY,
  sliderTime,
}) => {
  const point = svg?.current?.createSVGPoint();
  const [xStep, setXStep] = useState(0);
  const [initEventX1Delta, setInitEventX1Delta] = useState(0);
  const [isMoving, setIsMoving] = useState(false);

  // create xStep
  useEffect(() => {
    if (dates.length < 2) {
      // Degenerate single-column range (e.g. lookAhead with start === end):
      // there's no second date to derive the column span from, so assume a
      // one-day column instead of dereferencing dates[1].
      setXStep((timeStep * columnWidth) / 86400000);
      return;
    }
    const dateDelta =
      dates[1].getTime() -
      dates[0].getTime() -
      dates[1].getTimezoneOffset() * 60 * 1000 +
      dates[0].getTimezoneOffset() * 60 * 1000;
    const newXStep = (timeStep * columnWidth) / dateDelta;
    setXStep(newXStep);
  }, [columnWidth, dates, timeStep]);

  useEffect(() => {
    const handleMouseMove = async (event: MouseEvent) => {
      if (!ganttEvent.changedTask || !point || !svg?.current) return;
      event.preventDefault();

      point.x = event.clientX;
      const cursor = point.matrixTransform(
        svg?.current.getScreenCTM()?.inverse()
      );
      const { isChanged, changedTask } = handleTaskBySVGMouseEvent(
        cursor.x,
        ganttEvent.action as BarMoveAction,
        ganttEvent.changedTask,
        ganttEvent.type as string,
        xStep,
        timeStep,
        initEventX1Delta,
        rtl
      );
      if (isChanged) {
        let finalTask = { ...changedTask };
        const dragType = ganttEvent.type as string;
        if (
          changedTask.calendar &&
          (ganttEvent.action === "end" ||
            ganttEvent.action === "start" ||
            ganttEvent.action === "move")
        ) {
          const cal = finalTask.calendar!;
          if (dragType === "actual") {
            // --- Actual bar snapping ---
            if (ganttEvent.action === "end") {
              const snapped = snapToWorkingTime(finalTask.actualEnd, cal, "forward");
              finalTask.actualEnd = snapped;
              finalTask.actualx2 = taskXCoordinate(snapped, dates, columnWidth);
            } else if (ganttEvent.action === "start") {
              const snapped = snapToWorkingTime(finalTask.actualStart, cal, "forward");
              finalTask.actualStart = snapped;
              finalTask.actualx1 = taskXCoordinate(snapped, dates, columnWidth);
            } else {
              const snapped = snapToWorkingTime(finalTask.actualStart, cal, "forward");
              const delta = snapped.getTime() - finalTask.actualStart.getTime();
              finalTask.actualStart = snapped;
              finalTask.actualEnd = new Date(finalTask.actualEnd.getTime() + delta);
              finalTask.actualx1 = taskXCoordinate(finalTask.actualStart, dates, columnWidth);
              finalTask.actualx2 = taskXCoordinate(finalTask.actualEnd, dates, columnWidth);
            }
            // Recompute actual segments
            const actEnd = addToDate(startOfDate(finalTask.actualEnd, "day"), 1, "day");
            const actIntervals = getWorkingIntervals(finalTask.actualStart, actEnd, cal);
            const actSegs = actIntervals
              .map(iv => ({ x1: taskXCoordinate(iv.start, dates, columnWidth), x2: taskXCoordinate(iv.end, dates, columnWidth) }))
              .filter(s => s.x2 > s.x1);
            finalTask.actualSegments = actSegs.length > 0 ? actSegs : undefined;
            // Compute actual duration in real time
            const actDays = Math.max(1, Math.round((finalTask.actualEnd.getTime() - finalTask.actualStart.getTime()) / (1000 * 60 * 60 * 24)));
            finalTask.actualDuration = actDays;
          } else {
            // --- Planned bar snapping ---
            if (ganttEvent.action === "end") {
              const snapped = snapToWorkingTime(finalTask.end, cal, "forward");
              finalTask.end = snapped;
              finalTask.x2 = taskXCoordinate(snapped, dates, columnWidth);
            } else if (ganttEvent.action === "start") {
              const snapped = snapToWorkingTime(finalTask.start, cal, "forward");
              finalTask.start = snapped;
              finalTask.x1 = taskXCoordinate(snapped, dates, columnWidth);
            } else {
              const snapped = snapToWorkingTime(finalTask.start, cal, "forward");
              const delta = snapped.getTime() - finalTask.start.getTime();
              finalTask.start = snapped;
              finalTask.end = new Date(finalTask.end.getTime() + delta);
              finalTask.x1 = taskXCoordinate(finalTask.start, dates, columnWidth);
              finalTask.x2 = taskXCoordinate(finalTask.end, dates, columnWidth);
            }
            // Recompute planned segments
            const planEnd = addToDate(startOfDate(finalTask.end, "day"), 1, "day");
            const planIntervals = getWorkingIntervals(finalTask.start, planEnd, cal);
            const planSegs = planIntervals
              .map(iv => ({ x1: taskXCoordinate(iv.start, dates, columnWidth), x2: taskXCoordinate(iv.end, dates, columnWidth) }))
              .filter(s => s.x2 > s.x1);
            finalTask.plannedSegments = planSegs.length > 0 ? planSegs : undefined;
            // Compute planned duration in real time
            const planDays = Math.max(1, Math.round((finalTask.end.getTime() - finalTask.start.getTime()) / (1000 * 60 * 60 * 24)));
            finalTask.plannedDuration = planDays;
          }
        } else {
          // No calendar — still compute duration dynamically
          if (dragType === "actual") {
            const actDays = Math.max(1, Math.round((finalTask.actualEnd.getTime() - finalTask.actualStart.getTime()) / (1000 * 60 * 60 * 24)));
            finalTask.actualDuration = actDays;
          } else {
            const planDays = Math.max(1, Math.round((finalTask.end.getTime() - finalTask.start.getTime()) / (1000 * 60 * 60 * 24)));
            finalTask.plannedDuration = planDays;
          }
        }
        setGanttEvent({ action: ganttEvent.action, changedTask: finalTask, type: dragType });
      }
    };

    const handleMouseUp = async (event: MouseEvent) => {
      const { action, originalSelectedTask, changedTask, type } = ganttEvent;
      if (!changedTask || !point || !svg?.current || !originalSelectedTask)
        return;
      event.preventDefault();

      point.x = event.clientX;
      const cursor = point.matrixTransform(
        svg?.current.getScreenCTM()?.inverse()
      );
      const { changedTask: newChangedTask } = handleTaskBySVGMouseEvent(
        cursor.x,
        action as BarMoveAction,
        changedTask,
        type as string,
        xStep,
        timeStep,
        initEventX1Delta,
        rtl
      );

      const isNotLikeOriginal =
        originalSelectedTask.start !== newChangedTask.start ||
        originalSelectedTask.end !== newChangedTask.end ||
        originalSelectedTask.actualStart !== newChangedTask.actualStart ||
        originalSelectedTask.actualEnd !== newChangedTask.actualEnd ||
        originalSelectedTask.progress !== newChangedTask.progress;

      // Calendar snap: snap to nearest working time based on action direction
      const dropType = type as string;
      if (
        newChangedTask.calendar &&
        (action === "move" || action === "end" || action === "start")
      ) {
        const cal = newChangedTask.calendar;
        if (dropType === "actual") {
          // --- Actual bar snapping on drop ---
          if (action === "end") {
            const snappedEnd = snapToWorkingTime(newChangedTask.actualEnd, cal, "forward");
            newChangedTask.actualEnd = snappedEnd;
            newChangedTask.actualx2 = taskXCoordinate(snappedEnd, dates, columnWidth);
          } else if (action === "start") {
            const snappedStart = snapToWorkingTime(newChangedTask.actualStart, cal, "forward");
            if (snappedStart >= newChangedTask.actualEnd) {
              setFailedTask(originalSelectedTask);
              onCalendarError?.(newChangedTask, "No working time in selected range");
              setGanttEvent({ action: "" });
              setIsMoving(false);
              svg.current.removeEventListener("mousemove", handleMouseMove);
              svg.current.removeEventListener("mouseup", handleMouseUp);
              return;
            }
            newChangedTask.actualStart = snappedStart;
            newChangedTask.actualx1 = taskXCoordinate(snappedStart, dates, columnWidth);
          } else {
            const snappedStart = snapToWorkingTime(newChangedTask.actualStart, cal, "forward");
            const delta = snappedStart.getTime() - newChangedTask.actualStart.getTime();
            newChangedTask.actualStart = snappedStart;
            newChangedTask.actualEnd = new Date(newChangedTask.actualEnd.getTime() + delta);
            newChangedTask.actualx1 = taskXCoordinate(snappedStart, dates, columnWidth);
            newChangedTask.actualx2 = taskXCoordinate(newChangedTask.actualEnd, dates, columnWidth);
          }
          const actEnd = addToDate(startOfDate(newChangedTask.actualEnd, "day"), 1, "day");
          const actIntervals = getWorkingIntervals(newChangedTask.actualStart, actEnd, cal);
          const actSegs = actIntervals
            .map(iv => ({ x1: taskXCoordinate(iv.start, dates, columnWidth), x2: taskXCoordinate(iv.end, dates, columnWidth) }))
            .filter(s => s.x2 > s.x1);
          newChangedTask.actualSegments = actSegs.length > 0 ? actSegs : undefined;
          // Compute actual duration
          const actDays = Math.max(1, Math.round((newChangedTask.actualEnd.getTime() - newChangedTask.actualStart.getTime()) / (1000 * 60 * 60 * 24)));
          newChangedTask.actualDuration = actDays;
        } else {
          // --- Planned bar snapping on drop ---
          if (action === "end") {
            const snappedEnd = snapToWorkingTime(newChangedTask.end, cal, "forward");
            newChangedTask.end = snappedEnd;
            newChangedTask.x2 = taskXCoordinate(snappedEnd, dates, columnWidth);
          } else if (action === "start") {
            const snappedStart = snapToWorkingTime(newChangedTask.start, cal, "forward");
            if (snappedStart >= newChangedTask.end) {
              setFailedTask(originalSelectedTask);
              onCalendarError?.(newChangedTask, "No working time in selected range");
              setGanttEvent({ action: "" });
              setIsMoving(false);
              svg.current.removeEventListener("mousemove", handleMouseMove);
              svg.current.removeEventListener("mouseup", handleMouseUp);
              return;
            }
            newChangedTask.start = snappedStart;
            newChangedTask.x1 = taskXCoordinate(snappedStart, dates, columnWidth);
          } else {
            const snappedStart = snapToWorkingTime(newChangedTask.start, cal, "forward");
            const delta = snappedStart.getTime() - newChangedTask.start.getTime();
            newChangedTask.start = snappedStart;
            newChangedTask.end = new Date(newChangedTask.end.getTime() + delta);
            newChangedTask.x1 = taskXCoordinate(snappedStart, dates, columnWidth);
            newChangedTask.x2 = taskXCoordinate(newChangedTask.end, dates, columnWidth);
          }
          const planEnd = addToDate(startOfDate(newChangedTask.end, "day"), 1, "day");
          const planIntervals = getWorkingIntervals(newChangedTask.start, planEnd, cal);
          const planSegs = planIntervals
            .map(iv => ({ x1: taskXCoordinate(iv.start, dates, columnWidth), x2: taskXCoordinate(iv.end, dates, columnWidth) }))
            .filter(s => s.x2 > s.x1);
          newChangedTask.plannedSegments = planSegs.length > 0 ? planSegs : undefined;
          // Compute planned duration
          const planDays = Math.max(1, Math.round((newChangedTask.end.getTime() - newChangedTask.start.getTime()) / (1000 * 60 * 60 * 24)));
          newChangedTask.plannedDuration = planDays;
        }
      } else if (action === "move" || action === "end" || action === "start") {
        // No calendar — still compute duration on drop
        if (dropType === "actual") {
          const actDays = Math.max(1, Math.round((newChangedTask.actualEnd.getTime() - newChangedTask.actualStart.getTime()) / (1000 * 60 * 60 * 24)));
          newChangedTask.actualDuration = actDays;
        } else {
          const planDays = Math.max(1, Math.round((newChangedTask.end.getTime() - newChangedTask.start.getTime()) / (1000 * 60 * 60 * 24)));
          newChangedTask.plannedDuration = planDays;
        }
      }

      // remove listeners
      svg.current.removeEventListener("mousemove", handleMouseMove);
      svg.current.removeEventListener("mouseup", handleMouseUp);

      // Sync barTasks to final drag position BEFORE clearing ganttEvent (prevents reset flash)
      if (onBarTasksUpdate && isNotLikeOriginal) {
        onBarTasksUpdate(newChangedTask);
      }
      setGanttEvent({ action: "" });
      setIsMoving(false);

      // custom operation start
      let operationSuccess = true;
      if (
        (action === "move" || action === "end" || action === "start") &&
        onDateChange &&
        isNotLikeOriginal
      ) {
        try {
          const result = await onDateChange(
            newChangedTask,
            newChangedTask.barChildren
          );
          if (result !== undefined) {
            operationSuccess = result;
          }
        } catch (error) {
          operationSuccess = false;
        }
      } else if (onProgressChange && isNotLikeOriginal) {
        try {
          const result = await onProgressChange(
            newChangedTask,
            newChangedTask.barChildren
          );
          if (result !== undefined) {
            operationSuccess = result;
          }
        } catch (error) {
          operationSuccess = false;
        }
      }

      // If operation is failed - return old state
      if (!operationSuccess) {
        setFailedTask(originalSelectedTask);
      }
    };

    if (
      !isMoving &&
      (ganttEvent.action === "move" ||
        ganttEvent.action === "end" ||
        ganttEvent.action === "start" ||
        ganttEvent.action === "progress") &&
      svg?.current
    ) {
      svg.current.addEventListener("mousemove", handleMouseMove);
      svg.current.addEventListener("mouseup", handleMouseUp);
      setIsMoving(true);
    }
  }, [
    ganttEvent,
    xStep,
    initEventX1Delta,
    onProgressChange,
    timeStep,
    onDateChange,
    onCalendarError,
    projectCalendar,
    svg,
    isMoving,
    point,
    rtl,
    setFailedTask,
    setGanttEvent,
    onBarTasksUpdate,
    dates,
    columnWidth,
  ]);

  /**
   * Entry point for taskbar interaction events
   * (select, delete, drag/move start, click, double-click, hover, etc.).
   */
  const handleBarEventStart = async (
    action: GanttContentMoveAction,
    task: BarTask,
    event?: React.MouseEvent | React.KeyboardEvent,
    type?: string
  ) => {
    if (!event) {
      if (action === "select") {
        setSelectedTask(task.id);
      }
    }
    // Keyboard events
    else if (isKeyboardEvent(event)) {
      if (action === "delete") {
        if (onDelete) {
          try {
            const result = await onDelete(task);
            if (result !== undefined && result) {
              setGanttEvent({ action, changedTask: task });
            }
          } catch (error) {
            console.error("Error on Delete. " + error);
          }
        }
      }
    }
    // Mouse Events
    else if (action === "mouseenter") {
      if (!ganttEvent.action) {
        setGanttEvent({
          action,
          changedTask: task,
          originalSelectedTask: task,
          type: type,
        });
      }
    } else if (action === "mouseleave") {
      if (ganttEvent.action === "mouseenter") {
        setGanttEvent({ action: "" });
      }
    } else if (action === "dblclick") {
      !!onDoubleClick && onDoubleClick(task);
    } else if (action === "click") {
      !!onClick && onClick(task);
    }
    // Change task event start
    else if (action === "move") {
      if (!svg?.current || !point) return;
      point.x = event.clientX;
      const cursor = point.matrixTransform(
        svg.current.getScreenCTM()?.inverse()
      );
      if (type == "planned") setInitEventX1Delta(cursor.x - task.x1);
      else if (type == "actual") setInitEventX1Delta(cursor.x - task.actualx1);
      setGanttEvent({
        action,
        changedTask: task,
        originalSelectedTask: task,
        type: type,
      });
    } else {
      setGanttEvent({
        action,
        changedTask: task,
        originalSelectedTask: task,
        type: type,
      });
    }
  };

  const getArrows = (isCritical: boolean, criticalPathType?: string) => {
    return tasks.flatMap(_task => {
      // Follow the live drag preview only while this task is actively being
      // dragged — never on hover/select (otherwise hover would move the arrow,
      // since the hovered changedTask carries the bar's render y of 0).
      const a = ganttEvent.action;
      const isDraggingThis =
        (a === "move" || a === "start" || a === "end" || a === "progress") &&
        !!ganttEvent.changedTask &&
        ganttEvent.changedTask.id === _task.id;
      // Keep the stable vertical position (changedTask.y is 0).
      const _live = isDraggingThis
        ? { ...ganttEvent.changedTask!, y: _task.y }
        : _task;
      const task =
        _live.start.getTime() > 0 && _live.end.getTime() > 0
          ? _live
          : undefined;
      if (!task) {
        return [];
      }
      return task.barChildren
        .map(child => {
          const taskTo = tasks[child.index];
          // Both endpoints must have a drawable, in-range bar. A task fully
          // outside the visible date window clamps its x-coords (both to 0, the
          // left edge, or both to the right edge), which would otherwise send
          // the arrow shooting off toward the chart's left/right edge.
          const fromDrawable =
            task.x2 > task.x1 || task.actualx2 > task.actualx1;
          const toDrawable =
            !!taskTo &&
            (taskTo.x2 > taskTo.x1 || taskTo.actualx2 > taskTo.actualx1);
          if (fromDrawable && toDrawable) {
            const criticalTask = task.criticalPathArrows?.find(
              arrow =>
                arrow.taskId === taskTo.id &&
                (!!arrow.criticalPathType
                  ? arrow.criticalPathType === criticalPathType
                  : !criticalPathType)
            );

            const yFrom = task.y + taskHeight / 2;
            const yTo = taskTo.y + taskHeight / 2;
            const minY = Math.min(yFrom, yTo);
            const maxY = Math.max(yFrom, yTo);

            if (maxY < visibleStartY || minY > visibleEndY) {
              return null;
            }

            // Only render arrows matching the group
            if (!!criticalTask === isCritical) {
              return (
                <Arrow
                  key={`Arrow from ${task.id} to ${taskTo.id}${
                    isCritical ? "-critical" : ""
                  }`}
                  taskFrom={task}
                  taskTo={taskTo}
                  rowHeight={rowHeight}
                  dependencyType={child.dependencyType as Dependency}
                  taskHeight={taskHeight}
                  arrowIndent={arrowIndent}
                  arrowColor={criticalTask?.arrowColor || "#808080"}
                />
              );
            }
          }
          return null;
        })
        .filter(Boolean);
    });
  };

  // Calculate x position for the vertical line from sliderTime
  const lineX = sliderTime !== undefined ? taskXCoordinate(new Date(sliderTime), dates, columnWidth) : null;
  const totalHeight = tasks.length * rowHeight;




  return (
    <g className="content">
      {/* Vertical blue line for the slider-time marker */}
      {lineX !== null && lineX > 0 && (
        <line
          x1={lineX}
          y1={0}
          x2={lineX}
          y2={totalHeight}
          stroke="blue"
          strokeWidth={2}
          pointerEvents="none"
          opacity={0.8}
        />
      )}
      <g className="arrows">
        {/* Render non-critical arrows first */}
        {getArrows(false)}
        {/* Render secondary critical path arrows on top */}
        {getArrows(true, "secondary")}
        {/* Render primary critical path arrows on top */}
        {getArrows(true, "primary")}
      </g>
      <g className="bar" fontFamily={fontFamily} fontSize={fontSize}>
        {virtualItems.map(vi => {
          const _task = tasks[vi.index];
          // Use live drag preview for instant smooth updates (1 render/mousemove instead of 2)
          const task =
            ganttEvent.changedTask && ganttEvent.changedTask.id === _task.id
              ? { ...ganttEvent.changedTask, y: 0 }
              : { ..._task, y: 0 };
          if (!task && _task.typeInternal === "milestone") {
            return (
              <g key={_task.id} transform={`translate(0, ${vi.start})`}>
                <Milestone
                  task={task}
                  arrowIndent={arrowIndent}
                  taskHeight={taskHeight}
                  isProgressChangeable={false}
                  isDateChangeable={false}
                  isDelete={!_task.isDisabled}
                  onEventStart={handleBarEventStart}
                  isSelected={!!selectedTask && _task.id === selectedTask.id}
                  rtl={rtl}
                />
              </g>
            );
          }
          if (!task) {
            return (
              <g
                key={_task.id}
                transform={`translate(0, ${vi.start})`}
                style={{ height: taskHeight }}
              />
            );
          }
          return (
            <g key={task.id} transform={`translate(0, ${vi.start})`}>
              <TaskItem
                task={task}
                arrowIndent={arrowIndent}
                taskHeight={taskHeight}
                isProgressChangeable={!!onProgressChange && !task.isDisabled}
                isDateChangeable={!!onDateChange && !task.isDisabled}
                isDelete={!task.isDisabled}
                onEventStart={handleBarEventStart}
                isSelected={!!selectedTask && task.id === selectedTask.id}
                rtl={rtl}
              />
            </g>
          );
        })}
      </g>
    </g>
  );
};
